// « Am I on a phone? » for the Assistant (app-feel plan, builder B, 2026-10-01).
//
// Phone = max-width 767px — the same switch as Tailwind's `md:` (shared
// contract). A hook, not CSS, only where the phone gets a DIFFERENT TREE (the
// full-screen chat, the Inbox sheet): rendering both and hiding one would run
// every query and every composer twice.
import { useEffect, useSyncExternalStore } from 'react';

export const PHONE_QUERY = '(max-width: 767px)';

function subscribe(cb: () => void) {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const mq = window.matchMedia(PHONE_QUERY);
  mq.addEventListener?.('change', cb);
  return () => mq.removeEventListener?.('change', cb);
}
const snapshot = () => (typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(PHONE_QUERY).matches);

export function useIsPhone(): boolean {
  return useSyncExternalStore(subscribe, snapshot, () => false);
}

/**
 * ⚠️ THE SIGNAL TO THE SHELL: while a full-screen chat is open on a phone,
 * `<body data-adm-chat="open">`. The shell (builder A) hides its bottom bar
 * for it and treats `--adm-bottom-h` as 0 — the composer sits on the bottom
 * safe area, like WhatsApp. Removed when the chat closes or unmounts.
 */
export const CHAT_OPEN_ATTR = 'data-adm-chat';

export function useChatOpenSignal(open: boolean) {
  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    document.body.setAttribute(CHAT_OPEN_ATTR, 'open');
    return () => document.body.removeAttribute(CHAT_OPEN_ATTR);
  }, [open]);
}

/**
 * The part of the screen the keyboard leaves free. iOS does not shrink the
 * layout viewport for the keyboard, so a `fixed inset-0` composer would sit
 * under it; the visual viewport is what is really on screen.
 */
export function useVisualViewport(active: boolean): { height: number | null; top: number } {
  const read = () => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    return vv ? { height: vv.height, top: vv.offsetTop } : { height: null, top: 0 };
  };
  const sub = (cb: () => void) => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!active || !vv) return () => {};
    vv.addEventListener('resize', cb);
    vv.addEventListener('scroll', cb);
    return () => { vv.removeEventListener('resize', cb); vv.removeEventListener('scroll', cb); };
  };
  // A string snapshot keeps useSyncExternalStore from looping on a new object.
  const key = useSyncExternalStore(sub, () => JSON.stringify(read()), () => JSON.stringify({ height: null, top: 0 }));
  return JSON.parse(key) as { height: number | null; top: number };
}
